import React from 'react';
import withStyles from '@mui/styles/withStyles';
import Grid from './Grid';
import { InlineField, InlineFieldRightAlignChildren } from './Inline';
import MapMarkerIcon from '../icon/MapMarkerIcon';
import PhoneSquareIcon from '../icon/PhoneSquareIcon';
import EnvelopeSquareIcon from '../icon/EnvelopeSquareIcon';
import DeleteIcon from '../icon/DeleteIcon';
import EditIcon from '../icon/EditIcon';
import FaxIcon from '../icon/FaxIcon';
import UserCircleIcon from '../icon/UserCircleIcon';
import { IconButton } from '@mui/material';
import Label from './Label';
import cx from 'classnames';
import { joinDefined } from '../../util/strings';
import { listedPhones } from './PhoneNumberList';

//styles needs to be defined first
const styles = ({ spacing }) => ({
    contactCardContainer: {
        display: 'grid',
        gridGap: spacing(1),
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gridTemplateRows: 'auto',
        margin: `${spacing(1)} 0 ${spacing(2)}`
    },

    contactCard: {
        border: '1px solid #CDCDCD',
        borderRadius: '10px',
        padding: '10px 15px',
        background: 'white'
    },

    iconButton: {
        height: '32px',
        width: '32px'
    },

    icon: {
        height: '18px',
        width: '18px',
        verticalAlign: 'middle'
    },

    infoIcon: {
        color: '#CDCDCD'
    },

    detailsFont: {
        fontSize: '13px',
        fontWeight: '400',
        '& > span': {
            verticalAlign: 'text-top'
        }
    },

    wrappingDetails: {
        padding: '6px 0'
    },

    alignWithFloatingLabel: {
        marginTop: 17
    },

    alignWithLabel: {
        marginTop: 10
    },

    detailsGrid: {
        display: 'grid',
        gridAutoFlow: 'column',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gridTemplateRows: 'repeat(3, 1fr)'
    }
});

/**
 * container used to display contact cards
 */
export const ContactCardContainer = withStyles(styles)(({ children, ...props }) => {
    const { classes } = props;
    return (
        <Grid container className={classes.contactCardContainer}>
            {children}
        </Grid>
    );
});

export const formatContactAddress = contact =>
    joinDefined(
        [contact.AddressLine1, contact.AddressLine2, contact.Suburb, contact.State, contact.Country, contact.Postcode],
        ', '
    );

/**
 * contact card
 */
export const ContactCard = withStyles(styles)(
    ({ key, contact = sampleContact, canDelete = true, canEdit = true, onDelete, onEdit, ...props }) => {
        const { classes } = props;
        const formattedAddress = formatContactAddress(contact);
        const roles = Array.isArray(contact.Role) ? contact.Role.join(', ') : contact.Role;

        return (
            <Grid item key={key}>
                <Grid className={classes.contactCard}>
                    <InlineFieldRightAlignChildren>
                        <Label>{contact.Name}</Label>

                        {canEdit && onEdit && (
                            <IconButton
                                className={classes.iconButton}
                                title={'Edit'}
                                onClick={() => onEdit(key, contact)}
                                size="large"
                            >
                                <EditIcon className={classes.icon} />
                            </IconButton>
                        )}
                        {canDelete && onDelete && (
                            <IconButton
                                className={classes.iconButton}
                                title={'Delete'}
                                onClick={() => onDelete(key, contact)}
                                size="large"
                            >
                                <DeleteIcon className={classes.icon} />
                            </IconButton>
                        )}
                    </InlineFieldRightAlignChildren>

                    {!!roles && (
                        <InlineField lineHeight={'normal'}>
                            <span>
                                <UserCircleIcon className={cx(classes.icon, classes.infoIcon)} />
                            </span>
                            <span className={cx(classes.detailsFont, classes.wrappingDetails)}>
                                <span>{roles}</span>
                            </span>
                        </InlineField>
                    )}

                    <InlineField>
                        <span>
                            <MapMarkerIcon className={cx(classes.icon, classes.infoIcon)} />
                        </span>
                        <span className={classes.detailsFont}>
                            <span>{formattedAddress}</span>
                        </span>
                    </InlineField>

                    <div className={classes.detailsGrid}>
                        {listedPhones(contact.Phones).map((phone, i) => (
                            <InlineField lineHeight={2} key={i}>
                                <span>
                                    <PhoneSquareIcon className={cx(classes.icon, classes.infoIcon)} />
                                </span>
                                <span className={classes.detailsFont}>
                                    <span>
                                        {joinDefined([phone.Number, phone.Comment ? `(${phone.Comment})` : ''], ' ')}
                                    </span>
                                </span>
                            </InlineField>
                        ))}

                        {contact.Email && (
                            <InlineField lineHeight={2}>
                                <span>
                                    <EnvelopeSquareIcon className={cx(classes.icon, classes.infoIcon)} />
                                </span>
                                <span className={classes.detailsFont}>
                                    <span>{contact.Email}</span>
                                </span>
                            </InlineField>
                        )}

                        {contact.Fax && (
                            <InlineField lineHeight={2}>
                                <span>
                                    <FaxIcon className={cx(classes.icon, classes.infoIcon)} />
                                </span>
                                <span className={classes.detailsFont}>
                                    <span>{contact.Fax}</span>
                                </span>
                            </InlineField>
                        )}
                    </div>
                </Grid>
            </Grid>
        );
    }
);

const sampleContact = {
    Name: '',
    AddressLine1: '',
    AddressLine2: '',
    Suburb: '',
    Postcode: '',
    State: '',
    Country: '',
    Phone: '',
    Mobile: '',
    Email: '',
    Fax: ''
};
